import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

type JsonLike = Record<string, unknown>

/**
 * Attribute sources needed to resolve `{{prefix.path}}` template variables.
 *
 * Mirrors the canonical backend template-variable schema
 * (coval/src/modules/metrics/template_variables/schema.py) plus the legacy
 * prefixes handled by `attribute_utils.replace_with_attributes`. All fields are
 * optional so callers can build a context from whatever the call/simulation row
 * actually carries; unresolved templates are left untouched.
 */
export interface AttributeContext {
  /** {{agent.*}} — agent.attributes */
  agent_attributes?: JsonLike | null
  /** {{test_case.*}} / {{expected_output.*}} — test_case.expected_output_json */
  test_case_attributes?: JsonLike | null
  /** merged into test_case attributes as `expected_behaviors` */
  expected_behaviors?: string[] | null
  /** {{sim_wrapper.*}} — simulation wrapper external attributes */
  simulation_external_attributes?: JsonLike | null
  /** {{simulation_metadata.*}} (deprecated) — test_case.simulation_metadata_input */
  simulation_metadata?: JsonLike | null
  /** {{customer_metadata.*}} — run.customer_metadata */
  customer_metadata?: JsonLike | null
}

/**
 * Coerce an unknown value to a plain object usable as an attribute source, or
 * `undefined` when it is not a plain object (arrays, primitives, null). Handy
 * for building an {@link AttributeContext} from a loosely-typed run row.
 */
export const toAttributeRecord = (value: unknown): JsonLike | undefined => {
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    return value as JsonLike
  }
  return undefined
}

/**
 * Navigate through a nested object using dot notation and array indexing.
 *
 * Examples:
 *  getNestedValue({"a": {"b": 1}}, "a.b") -> 1
 *  getNestedValue({"users": {"sam": {"card": "1234"}}}, "users.sam.card") -> "1234"
 *  getNestedValue({"test": ["1", "2"]}, "test[0]") -> "1"
 *  getNestedValue({"test": ["1", "2"]}, "test") -> ["1", "2"]
 */
const getNestedValue = (
  data: JsonLike | null | undefined,
  path: string,
): unknown => {
  if (!data) {
    return null
  }

  if (!path) {
    return data
  }

  const parts = path.split(".")
  let current: unknown = data

  for (const part of parts) {
    // Check if this part has array indexing (e.g., "test[0]" or "arr[1]")
    const arrayMatch = part.match(/^([^[]+)(\[(\d+)\])$/)

    if (arrayMatch) {
      // Extract the key and index
      const key = arrayMatch[1]
      const index = parseInt(arrayMatch[3], 10)

      // Navigate to the key first
      if (
        typeof current === "object" &&
        current !== null &&
        !Array.isArray(current) &&
        key in current
      ) {
        current = (current as JsonLike)[key]
      } else {
        return null
      }

      // Then access the array index
      if (Array.isArray(current) && index >= 0 && index < current.length) {
        current = current[index]
      } else {
        return null
      }
    } else if (
      // Regular object key access
      typeof current === "object" &&
      current !== null &&
      !Array.isArray(current) &&
      part in current
    ) {
      current = (current as JsonLike)[part]
    } else {
      return null
    }
  }

  return current
}

// Pattern matches {{prefix}} or {{prefix.path.to.value}} where prefix is the
// source type. Optional surrounding whitespace is allowed so the canonical
// stored form ({{ prefix.key }}) matches as well as the compact form. Using
// [^{}]+ for the path ensures inner templates are matched first, e.g. in
// {{agent.users.{{test_case.username}}.email}} only {{test_case.username}}
// matches on the first pass.
export const ATTRIBUTE_TEMPLATE_PATTERN =
  /\{\{\s*(agent|test_case|expected_output|simulation_metadata|sim_wrapper|customer_metadata)(?:\.([^{}]+))?\s*\}\}/g

/**
 * Replace template variables in a string with values from the call's attribute
 * context. Supports nested paths, array indexing, and multi-pass resolution for
 * nested templates.
 *
 * Examples:
 *  {{agent.users.sam.creditCard}}
 *  {{test_case.nested.value}}
 *  {{customer_metadata.region}}
 *  {{sim_wrapper.wrapper_type.attribute}}
 *  {{ agent.users.{{ test_case.username }}.email }}
 *  {{test_case.test[0]}} - array indexing
 *  {{agent}} - root-level access (returns entire object)
 *  {{test_case.test}} - array access without indexing (returns array as string)
 */
const getAttributeSource = (
  prefix: string,
  context: AttributeContext,
): JsonLike | null | undefined => {
  // Build test_case_attributes with expected_behaviors included
  const testCaseAttributes: JsonLike = {
    ...(context.test_case_attributes ?? {}),
    expected_behaviors: context.expected_behaviors ?? [],
  }

  switch (prefix) {
    case "agent":
      return context.agent_attributes
    case "test_case":
    case "expected_output":
      return testCaseAttributes
    case "sim_wrapper":
      return context.simulation_external_attributes
    // Deprecating simulation_metadata in favor of sim_wrapper
    case "simulation_metadata":
      return context.simulation_metadata
    case "customer_metadata":
      return context.customer_metadata
    default:
      return undefined
  }
}

/**
 * Resolve a single `{{prefix.path}}` token to its substitution string, or null
 * when it can't be resolved (unknown prefix, missing source, or missing value)
 * so callers can keep the literal token. Shared by the string replacer and the
 * node renderer that highlights substituted spans.
 */
export const resolveAttributeToken = (
  prefix: string,
  rawPath: string | undefined,
  context: AttributeContext,
): string | null => {
  const path = (rawPath ?? "").trim()
  const source = getAttributeSource(prefix, context)

  if (!source) {
    return null // unknown prefix or no source data
  }

  // If no path provided, return the entire source as a string
  if (!path) {
    try {
      return JSON.stringify(source)
    } catch {
      return "[object]"
    }
  }

  // Get the value using nested path navigation
  const value = getNestedValue(source, path)
  if (value === null || value === undefined) {
    return null
  }

  // Handle objects and arrays with JSON.stringify
  if (typeof value === "object" || Array.isArray(value)) {
    try {
      return JSON.stringify(value)
    } catch {
      // Handle circular references or other stringify errors
      return "[object]"
    }
  }

  // For primitive values, use String conversion
  return stringifyUnknownValue(value)
}

export const replaceWithAttributes = (
  baseStr: string,
  context: AttributeContext,
  maxPasses = 10,
): string => {
  const replaceMatch = (
    original: string,
    prefix: string,
    rawPath: string | undefined,
  ): string => resolveAttributeToken(prefix, rawPath, context) ?? original

  // Multi-pass resolution for nested templates like
  // {{agent.users.{{test_case.username}}.email}}
  let result = baseStr
  for (let i = 0; i < maxPasses; i += 1) {
    const newResult = result.replace(ATTRIBUTE_TEMPLATE_PATTERN, replaceMatch)
    if (newResult === result) {
      break // No more replacements, we're done
    }
    result = newResult
  }

  return result
}
